import { inject, ref, watch, type Ref } from 'vue'
import { GRID_HTTP_INJECTION_KEY, parsePageResult } from '@zoryn/components/grid'
import type { HttpRequest } from '@zoryn/network'
import type { FilterPayloadNode } from '@zoryn/components/grid'
import type { SystemParamField } from './rules'

const PARAMS_QUERY_URL = '/system/params/queryByExamplePage'

export function normalizeParamRow(row: Record<string, unknown>): SystemParamField | null {
  const field = String(
    row.paramKey ?? row.field ?? row.paramField ?? row.paramCode ?? ''
  )
  const name = String(row.paramName ?? row.name ?? row.title ?? field)
  const columnType = String(row.columnType ?? row.type ?? row.colType ?? 'input')
  if (!field) {
    return null
  }
  return {
    system:
      row.systemFlag != null
        ? String(row.systemFlag)
        : row.system != null
          ? String(row.system)
          : undefined,
    module: row.module != null ? String(row.module) : undefined,
    field,
    name,
    columnType,
    dataSource:
      row.columnTypeValue != null
        ? String(row.columnTypeValue)
        : row.dataSource != null
          ? String(row.dataSource)
          : row.source != null
            ? String(row.source)
            : undefined,
  }
}

function extractParamList(data: unknown): SystemParamField[] {
  if (Array.isArray(data)) {
    return data
      .map((item) => normalizeParamRow(item as Record<string, unknown>))
      .filter((item): item is SystemParamField => item != null)
  }
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    const list = record.list ?? record.rows ?? record.data ?? record.records
    if (Array.isArray(list)) {
      return extractParamList(list)
    }
  }
  return []
}

export function useRulesParams(options: {
  system: Ref<string>
  module: Ref<string | undefined>
}) {
  const request = inject<HttpRequest | null>(GRID_HTTP_INJECTION_KEY, null)
  const fieldList = ref<SystemParamField[]>([])
  const loading = ref(false)
  const error = ref<Error | null>(null)

  async function loadFields() {
    if (!request) {
      fieldList.value = []
      error.value = new Error('[ZRules] 未配置 HttpRequest')
      return
    }

    loading.value = true
    error.value = null
    try {
      const params: Record<string, unknown> = {
        currentPage: 1,
        pageSize: 9999,
        pageFlag: false,
        filters: [] as FilterPayloadNode[],
      }
      const filters: FilterPayloadNode[] = [
        { operator: 'eq', field: 'systemFlag', value: options.system.value },
      ]
      if (options.module.value) {
        filters.push({
          operator: 'eq',
          field: 'module',
          value: options.module.value,
        })
      }
      params.filters = JSON.stringify(filters)

      const res = await request.get({ url: PARAMS_QUERY_URL, params })
      const { list } = parsePageResult(res as never)
      fieldList.value = extractParamList(list)
    } catch (err) {
      fieldList.value = []
      error.value = err instanceof Error ? err : new Error(String(err))
    } finally {
      loading.value = false
    }
  }

  watch(
    () => [options.system.value, options.module.value],
    () => {
      void loadFields()
    },
    { immediate: true }
  )

  return {
    fieldList,
    loading,
    error,
    reload: loadFields,
  }
}
